import {
  BookIcon,
  ChevronRightSmallIcon,
  CoinsStackedIcon,
  UsersPlusIcon,
} from 'assets/icons';
import { formatBytes } from 'helpers/storage';
import { goToPage, RouteName } from 'routes';
import { useStorageUsage } from './use-storage-usage';
import styles from './vault-info.module.scss';

// From this much of the plan used, the card shows the storage meter (design
// note: "When vault is at 90% capacity or above, switch Vault info variant to
// show storage").
const STORAGE_WARNING_PERCENT = 90;

interface VaultInfoProps {
  vault: VaultType;
  // Opens the share dialog. The button is hidden when not given (people who
  // can't invite to this vault).
  onShare?: () => void;
}

// The current vault at the foot of the account menu: its cover and name, a
// Share button, and — once it is nearly full — its storage use.
export const VaultInfo = ({ vault, onShare }: VaultInfoProps) => {
  const usage = useStorageUsage(vault);
  const cover = vault.coverThumbnailUrl || vault.coverUrl;
  const showStorage = !!usage && usage.percentUsed >= STORAGE_WARNING_PERCENT;

  return (
    <div className={styles.wrapper}>
      <div className={styles.card}>
        <div className={styles.vaultRow}>
          <div className={styles.vault}>
            <span className={styles.cover}>
              {cover ? <img src={cover} alt="" /> : <BookIcon />}
            </span>
            <div className={styles.vaultTitle}>
              <span className={styles.vaultLabel}>Current Vault</span>
              <span className={styles.vaultName} title={vault.title}>
                {vault.title}
              </span>
            </div>
          </div>
          {onShare && (
            <button type="button" className={styles.share} onClick={onShare}>
              Share Vault
              <UsersPlusIcon />
            </button>
          )}
        </div>

        {showStorage && usage && (
          <>
            <div className={styles.separator} />
            <div className={styles.storage}>
              <div className={styles.storageHeader}>
                <span className={styles.storageLabel}>
                  <CoinsStackedIcon />
                  Storage
                </span>
                <span className={styles.storageSwap}>
                  <span
                    className={styles.storagePercent}
                  >{`${usage.percentUsed}% used`}</span>
                  <button
                    type="button"
                    className={styles.getMore}
                    onClick={() => goToPage(RouteName.Store)}
                  >
                    Get more
                    <ChevronRightSmallIcon />
                  </button>
                </span>
              </div>
              <div
                className={styles.track}
                role="progressbar"
                aria-valuenow={usage.percentUsed}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Storage used"
                title={`${formatBytes(usage.usedBytes)} used of ${formatBytes(vault.storageLimit)}`}
              >
                <div
                  className={styles.fill}
                  style={{ width: `${usage.percentUsed}%` }}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
